import React, { useEffect } from 'react';
import { useI18n } from 'react-simple-i18n';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import slSI from '../assets/img/flag/si.png';
import enUK from '../assets/img/flag/gb.png';
import rosEng from '../assets/img/new-ros-eng.png';
import rosSlo from '../assets/img/new-ros-slo.png';
import NorwayGrants from '../assets/img/NorwayGrants.png';
import element from '../assets/img/element.png';
import hamburger from '../assets/img/hamburger.svg';
import Cover from '../assets/img/cover';
import appstate, { getLocaledString } from '../appstate';
import { ArrowDownComponent } from './arrowDownComponent';
import { ThreeArrowDownComponent } from './threeArrowDownComponent';

export default function HeaderComponent() {
	const { t } = useI18n();

	const { pathname } = useLocation();

	const [locationChanged, setLocationChanged] = React.useState(false);

	useEffect(() => {
		setLocationChanged(true);

		// change page title
		document.title = `Prometej - ${t(`pageTitles.${location.pathname}`)}`;

		window.scrollTo(0, 0);

		handleScroll();

		setTimeout(() => {
			setLocationChanged(false);
		}, 200);
	}, [pathname]);

	const marginAroundTitle = 85;
	const titleHeightThreshold = 150;
	const headerStyleChangeThreshold = 190;

	const [titleHeight, setTitleHeight] = React.useState(titleHeightThreshold);
	const [hamburgerOpen, setHamburgerOpen] = React.useState(false);

	const [scrolledHeader, setScrolledHeader] = React.useState(true);

	const handleScroll = () => {
		const scrollPos = Math.max(window.scrollY, 0);

		const newTitleHeight = !(window.location.pathname === '/')
			? 100
			: Math.max(titleHeightThreshold - scrollPos / 4.5, 50) + 50;

		setTitleHeight(newTitleHeight);

		// Should have border
		setScrolledHeader(window.scrollY > headerStyleChangeThreshold);
	};

	useEffect(() => {
		handleScroll();

		window.removeEventListener('scroll', handleScroll);

		window.addEventListener('scroll', handleScroll, { passive: true });

		document.body.addEventListener('click', closeHamburger);

		return () => {
			return window.removeEventListener('scroll', handleScroll);
			return window.removeEventListener('click', closeHamburger);
		};
	}, []);

	const titleStyle = {
		transform: `scale(${titleHeight / 2}%)`,
		fontWeight: 600,
	};

	const setLanguage = (lang: string) => {
		if (localStorage.getItem('language') !== lang) {
			localStorage.setItem('language', lang);
			window.location.replace(window.location.href);
		}
	};

	const openHamburger = () => {
		setHamburgerOpen(true);
	};

	const closeHamburger = () => {
		setHamburgerOpen(false);
	};

	const languageFriendlyName = (lang: string) => {
		return lang === 'enUK' ? 'English' : 'Slovenščina';
	};

	const getCountryFlag = (lang: string) => {
		if (lang === 'enUK') {
			return enUK;
		} else if (lang === 'slSI') {
			return slSI;
		}

		return enUK;
	};

	const toggleLanguage = () => {
		setLanguage(appstate.currentLanguage() === 'enUK' ? 'slSI' : 'enUK');
	};

	const coverStyle = () => {
		return {
			height: 'calc(100vh - 85px - 25px)',
			width: '100%',
			justifyContent: 'center',
			alignItems: 'center',
		};
	};

	const coverScaleStyle = () => {
		return {
			height: '100%',
			marginTop: '-50px',
			width: 'auto',
		};
	};

	const isMobile = () => {
		return window.innerWidth < 768;
	};

	const HamburgerSvg = () => {
		return (
			<svg viewBox="0 0 500 500" xmlns="http://www.w3.org/2000/svg">
				<defs></defs>
				<path
					className={'transition'}
					style={{
						transform: hamburgerOpen
							? 'rotate(45deg) translate(120px, -40px)'
							: 'rotate(0deg)',
					}}
					fill="#000"
					d="M 26.724 32.601 L 26.724 96.601 L 474.724 96.601 L 474.724 32.601 L 26.724 32.601 Z"
				></path>
				<path
					className={'transition'}
					style={{
						opacity: hamburgerOpen ? '0' : '1',
					}}
					fill="#000"
					fillOpacity={'0.6'}
					d="M 26.724 178.006 L 26.724 242.006 L 474.724 242.006 L 474.724 178.006 L 26.724 178.006 Z"
				></path>
				<path
					className={'transition'}
					style={{
						transform: hamburgerOpen
							? 'rotate(-45deg) translate(-280px, 0px)'
							: 'rotate(0deg)',
					}}
					fill="#000"
					fillOpacity={'0.45'}
					d="M 26.724 329.668 L 26.724 393.668 L 474.724 393.668 L 474.724 329.668 L 26.724 329.668 Z"
				></path>
			</svg>
		);
	};

	const tmpx = getLocaledString(
		'– Zaposleni in zaposlene –',
		'– Employees –',
	);

	return (
		<>
			{/* Top margin */}
			<div className={`bg-white block`}>
				<div
					style={{ height: `${marginAroundTitle}px` }}
					className={
						'z-50 sticky select-none bottom-0 max-w-8xl gap-6 mx-auto flex flex-row w-full items-center flex flex-row justify-between px-6 md:px-12'
					}
				>
					<a
						href="https://www.gov.si/drzavni-organi/vladne-sluzbe/sluzba-vlade-za-razvoj-in-evropsko-kohezijsko-politiko"
						target={'_blank'}
						rel="noreferrer"
					>
						<img
							className={'select-none'}
							src={
								appstate.currentLanguage() === 'enUK'
									? rosEng
									: rosSlo
							}
							style={{
								height: isMobile() ? '28px' : '26px',
								width: 'auto',
							}}
							alt={
								appstate.currentLanguage() === 'enUK'
									? 'SLUZBA VLADE REPUBLIKE SLOVENIJE ZA RAZVOJ IN EVROPSKO KOHEZIJSKO POLITIKO'
									: 'GOVERNMENT OFFICE FOR DEVELOPMENT AND EUROPEAN COHESION POLICY'
							}
						/>
					</a>

					<a
						href="https://www.norwaygrants.com/"
						target={'_blank'}
						rel="noreferrer"
					>
						<img
							src={NorwayGrants}
							style={{
								height: isMobile() ? '36px' : '52px',
								width: 'auto',
							}}
							alt="Norway Grants"
						/>
					</a>
				</div>
			</div>

			{/* Navigation */}
			<div
				style={{ height: isMobile() ? '45px' : '90px' }}
				className={`transition z-40 w-full text-base flex flex-row gap-4 items-center mt-6 md:mt-0 justify-center mb-0`}
				onClick={() => {
					if (document.body.clientWidth < 768) {
						// go to "/" route
						document.location.href = '/';
					}
				}}
			>
				<div
					className={
						'max-w-8xl mx-auto flex-row w-full items-center px-6 md:px-12 hidden md:flex'
					}
					style={{ marginTop: '-80px' }}
				>
					<div className={'flex-2 flex gap-6 justify-start'}></div>
					<Link
						to={'/'}
						className={`text-7xl md:text-8xl py-2 flex-1 flex justify-center font-bold z-50 ${
							(pathname === '/' && scrolledHeader) ||
							pathname !== '/'
								? 'cursor-pointer'
								: 'cursor-default'
						}`}
						onClick={() => {
							if (pathname === '/' && scrolledHeader) {
								window.scrollTo({ top: 0, behavior: 'smooth' });
							}
						}}
					>
						<div
							style={{
								transform: 'scale(0.5)',
								fontWeight: '800',
							}}
							className={
								'text-7xl md:text-8xl flex-1 flex justify-center text-primary-500 transition opacity-100 transparent-outlined'
							}
						>
							<span>{t('header.title')}</span>
						</div>
					</Link>
					<div className={'flex-2 flex gap-6 justify-end'}>
						{/*<div
							className={
								'flex flex-row gap-6 relative cursor-pointer text-2xl p-x py-2'
							}
							onClick={(e) => {
								e.stopPropagation();
								return toggleLanguage();
							}}
						>
							<img
								src={getCountryFlag(appstate.currentLanguage())}
								className={'w-6 shadow'}
								alt=""
							/>
						</div>*/}
					</div>
				</div>
				<div className={'flex md:hidden -mt-20'}>
					{/* Hamburger menu mobile */}
					<div
						style={{
							transform: 'scale(1)',
							fontWeight: '800',
						}}
						className={
							'text-4xl md:text-8xl flex-1 flex justify-center text-primary-500 transition opacity-100 transparent-outlined'
						}
					>
						<span>{t('header.title')}</span>
					</div>
				</div>
			</div>

			<>
				<div
					className={
						'text-xl md:text-4xl w-full text-center text-primary-500 relative z-10 mb-4 md:mb-0 px-6 select-none font-bold'
					}
					style={{ marginTop: '-3.5vh' }}
					id={'ddt'}
				>
					{pathname === '/' ? t('home.caption') : ''}

					{pathname === '/employees-and-employers' ? tmpx : ''}

					{appstate.currentLanguage() === 'slSI' &&
					pathname === '/quiz1'
						? '(Ne)uspešnost naslavljanja poklicnega in zasebnega življenja v organizaciji'
						: ''}

					{appstate.currentLanguage() === 'enUK' &&
					pathname === '/quiz1'
						? '(Un)successful Addressal of Work-life Balance in My Organization'
						: ''}

					{appstate.currentLanguage() === 'slSI' &&
					pathname === '/quiz2'
						? 'Stres na delovnem mestu'
						: ''}
					{appstate.currentLanguage() === 'enUK' &&
					pathname === '/quiz2'
						? 'Stress in the workplace'
						: ''}

					{appstate.currentLanguage() === 'slSI' &&
					pathname === '/leaders-in-org'
						? '– Vodje v organizaciji –'
						: ''}

					{appstate.currentLanguage() === 'enUK' &&
					pathname === '/leaders-in-org'
						? '– Managers –'
						: ''}
				</div>
				{pathname === '/' && (
					<div className={'hidden md:flex'} style={coverStyle()}>
						<Cover
							style={coverScaleStyle()}
							className={'mx-auto max-w-full'}
						/>
					</div>
				)}
				{pathname === '/' && (
					<div
						className={
							'absolute bottom-0 right-0 m-4 mr-16 w-24 h-auto p-4 cursor-pointer hidden md:block'
						}
						onClick={() => {
							const el = document.getElementById('frst-sc');

							if (el) {
								el.scrollIntoView({
									behavior: 'smooth',
								});
							}
						}}
					>
						<ThreeArrowDownComponent />
					</div>
				)}
			</>
		</>
	);
}
